/**
 * Find Page Entry Point
 * Main entry for the find animals page
 */

import Alpine from 'alpinejs'
import '../styles/main.css'

import { initSentry } from './config/sentry'

initSentry()

// Import stores
import { registerAuthStore } from './stores/auth.store'
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in ReturnType<typeof createReportsStore>
import { registerReportsStore, createReportsStore } from './stores/reports.store'
import { registerToastsStore } from './stores/toasts.store'

// Import components
import { registerModalComponents } from './components/modal'
import { registerToastComponents } from './components/toast'
import { registerFilterComponents } from './components/filters'
import { registerMapComponents } from './components/map'

// Import services and types
import type { Report, GeoLocation } from './types'
import { formatRelativeTime } from './utils/format'
import { getDistance, formatDistance } from './utils/geo'

// ============================================================================
// Find Page Component
// ============================================================================

interface FindPageData {
  // View state
  view: 'map' | 'list'
  loading: boolean
  error: string | null

  // Map state
  mapReady: boolean
  userLocation: GeoLocation | null
  searchRadius: number

  // Selected report
  selectedReport: Report | null
  showReportModal: boolean

  // Methods
  init(): Promise<void>
  setView(view: 'map' | 'list'): void
  selectReport(report: Report): void
  closeReportModal(): void
  getUserLocation(): Promise<void>
  searchNearby(): Promise<void>
  getDistanceFromUser(report: Report): string
  formatTime(date: unknown): string
  openDirections(report: Report): void
}

function findPage(): FindPageData {
  return {
    view: 'map',
    loading: true,
    error: null,

    mapReady: false,
    userLocation: null,
    searchRadius: 25, // km

    selectedReport: null,
    showReportModal: false,

    async init(): Promise<void> {
      // Initialize stores
      const authStore = Alpine.store('auth') as { init(): void }
      authStore.init()

      const reportsStore = Alpine.store('reports')
      reportsStore.init({ status: 'new' })

      // Load countries for filters
      await reportsStore.loadCountries()

      // Try to get user location
      await this.getUserLocation()

      this.loading = false
    },

    setView(view: 'map' | 'list'): void {
      this.view = view
    },

    selectReport(report: Report): void {
      this.selectedReport = report
      this.showReportModal = true
    },

    closeReportModal(): void {
      this.showReportModal = false
      this.selectedReport = null
    },

    async getUserLocation(): Promise<void> {
      return new Promise((resolve) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime safety for older browsers
        if (!navigator.geolocation) {
          resolve()
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.userLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }
            resolve()
          },
          () => {
            // User denied or error - use default location
            resolve()
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          }
        )
      })
    },

    async searchNearby(): Promise<void> {
      if (!this.userLocation) {
        const toasts = Alpine.store('toasts') as { warning(msg: string): void }
        toasts.warning('Please enable location access to search nearby')
        return
      }

      const reportsStore = Alpine.store('reports')
      await reportsStore.fetchNearby(
        this.userLocation.latitude,
        this.userLocation.longitude,
        this.searchRadius
      )
    },

    getDistanceFromUser(report: Report): string {
      if (!this.userLocation || !report.location) {
        return ''
      }

      const distance = getDistance(
        this.userLocation.latitude,
        this.userLocation.longitude,
        report.location.latitude,
        report.location.longitude
      )

      return formatDistance(distance)
    },

    formatTime(date: unknown): string {
      return formatRelativeTime(date as Date)
    },

    openDirections(report: Report): void {
      if (!report.location) {
        return
      }
      const url = `https://www.google.com/maps/dir/?api=1&destination=${String(report.location.latitude)},${String(report.location.longitude)}`
      window.open(url, '_blank')
    },
  }
}

// ============================================================================
// Report Card Component
// ============================================================================

interface ReportCardData {
  report: Report | null
  expanded: boolean
  toggle(): void
  getStatusClass(): string
  getConditionClass(): string
  getAnimalIcon(): string
}

function reportCard(): ReportCardData {
  return {
    report: null,
    expanded: false,

    toggle(): void {
      this.expanded = !this.expanded
    },

    getStatusClass(): string {
      if (!this.report) {
        return ''
      }
      const classes: Record<string, string> = {
        pending: 'badge-pending',
        new: 'badge-new',
        investigating: 'badge-investigating',
        resolved: 'badge-resolved',
        closed: 'badge-closed',
      }
      return classes[this.report.status] ?? ''
    },

    getConditionClass(): string {
      if (!this.report) {
        return ''
      }
      const classes: Record<string, string> = {
        healthy: 'text-green-600',
        injured: 'text-red-600',
        sick: 'text-orange-600',
        unknown: 'text-gray-600',
      }
      return classes[this.report.condition ?? 'unknown'] ?? 'text-gray-600'
    },

    getAnimalIcon(): string {
      if (!this.report) {
        return 'pets'
      }
      const icons: Record<string, string> = {
        dog: 'pets',
        cat: 'pets',
        bird: 'flutter_dash',
        other: 'cruelty_free',
      }
      return icons[this.report.animalType] ?? 'pets'
    },
  }
}

// ============================================================================
// Search Component
// ============================================================================

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function searchComponent() {
  return {
    query: '',
    searching: false,
    results: [] as GeoLocation[],

    async search(): Promise<void> {
      if (!this.query.trim()) {
        return
      }

      this.searching = true

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.query)}`
        )
        const data = (await response.json()) as { lat: string; lon: string; display_name: string }[]

        this.results = data.slice(0, 5).map((item) => ({
          latitude: parseFloat(item.lat),
          longitude: parseFloat(item.lon),
          name: item.display_name,
        }))
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        this.searching = false
      }
    },

    selectResult(result: GeoLocation & { name?: string }): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      ;(this as any).$dispatch?.('location-selected', result)
      this.query = result.name ?? ''
      this.results = []
    },

    clear(): void {
      this.query = ''
      this.results = []
    },
  }
}

// ============================================================================
// Initialize Application
// ============================================================================

// Register stores
registerAuthStore()
registerReportsStore()
registerToastsStore()

// Register components
registerModalComponents()
registerToastComponents()
registerFilterComponents()
registerMapComponents()

// Register page components
Alpine.data('findPage', findPage)
Alpine.data('reportCard', reportCard)
Alpine.data('searchComponent', searchComponent)

// Start Alpine
Alpine.start()
